import type {
	EditorDictionary,
	ISecodaEditor,
	MenuItem,
} from '@repo/secoda-editor';
import type { EditorView } from 'prosemirror-view';

import { Box, createStyles } from '@mantine/core';
import type { CommandFactory } from '@repo/secoda-editor/lib/Extension';
import { Toolbar } from '@repo/secoda-editor/types';
import { useCallback, useEffect } from 'react';
import { VisuallyHidden } from '../VisuallyHidden';
import { CommandMenuDivider } from './CommandMenuDivider';
import { useFloatingToolbarContext } from './context';
import { FloatingToolbar } from './FloatingToolbar';
import { LinkInput } from './LinkInput';
import { useCommandMenu } from './useCommandMenu';
import { useToolbarSearch } from './useToolbarSearch';

const useStyles = createStyles((theme) => ({
	scroll: {
		overflow: 'hidden',
		overflowY: 'auto',
		padding: `${theme.spacing['2xs']} 0`,
	},
}));

export type CommandMenuType =
	| 'block-menu-container'
	| 'emoji-menu-container'
	| 'mention-menu-container';

export interface CommandMenuProps<T extends MenuItem = MenuItem> {
	id: CommandMenuType;
	rtl: boolean;
	commands: Record<string, CommandFactory>;
	dictionary: EditorDictionary;
	view: EditorView;
	search: string;
	loading?: boolean;
	uploadFile?: (file: File) => Promise<string>;
	onFileUploadStart?: () => void;
	onFileUploadStop?: () => void;
	onShowToast?: (message: string, id: string) => void;
	editor: ISecodaEditor;
	onClose: () => void;
	renderMenuItem: (
		item: T,
		index: number,
		props: Record<string, unknown>
	) => React.ReactNode;
	filterable?: boolean;
	items: T[];
	trigger?: string;
	onTrackEvent?: (
		eventName: string,
		properties?: Record<string, string>
	) => void;
	isTemplate?: boolean;
}

function CommandMenuInternal({
	items,
	trigger,
	renderMenuItem,
	view,
	commands,
	uploadFile,
	search,
	filterable,
	onClose,
	dictionary,
	onFileUploadStart,
	onFileUploadStop,
	onShowToast,
	onTrackEvent,
	isTemplate,
	editor,
}: CommandMenuProps) {
	const { classes } = useStyles();

	const { getItemProps, activeIndex, elementsRef, reset, refs } =
		useFloatingToolbarContext();

	const { clearSearch, filteredItems } = useToolbarSearch({
		view,
		searchTerm: search,
		trigger,
		commands,
		items,
		filterable,
		uploadEnabled: !!uploadFile,
	});

	const onLinkToolbarOpen = useCallback(() => {
		editor.openToolbar(Toolbar.Link);
	}, [editor]);

	const onPlaceholderToolbarOpen = useCallback(() => {
		editor.openToolbar(Toolbar.Placeholder);
	}, [editor]);

	const {
		fileUploadInputRef,
		handleFilePicked,
		handleItemClick,
		embedItem,
		insertChartBlockItem,
		insertQueryBlockItem,
		insertBlock,
	} = useCommandMenu({
		view,
		commands,
		onClearSearch: clearSearch,
		onClose,
		onLinkToolbarOpen,
		onPlaceholderToolbarOpen,
		dictionary,
		onFileUploadStart,
		onFileUploadStop,
		uploadFile,
		onShowToast,
		onTrackEvent,
		isTemplate,
	});

	useEffect(() => {
		// Find the first non-separator item to auto-select
		const firstSelectableIndex = filteredItems.findIndex(
			(item) => item.name !== 'separator'
		);

		// Only auto-select if we found a valid non-separator item
		if (firstSelectableIndex >= 0) {
			reset(firstSelectableIndex);
		}
	}, [
		embedItem,
		insertChartBlockItem,
		insertQueryBlockItem,
		filteredItems,
		reset,
	]);

	return (
		<>
			{embedItem && (
				<LinkInput
					placeholder={
						embedItem.title
							? dictionary.pasteLinkWithTitle(embedItem.title)
							: dictionary.pasteLink
					}
					autoFocus
					dictionary={dictionary}
					embedItem={embedItem}
					onInsertBlock={insertBlock}
					onClose={onClose}
					onShowToast={onShowToast}
				/>
			)}
			{!embedItem && !insertChartBlockItem && !insertQueryBlockItem && (
				<Box className={classes.scroll}>
					{filteredItems.map((item, index) => {
						if (item.name === 'separator') {
							return (
								<CommandMenuDivider
									// eslint-disable-next-line react/no-array-index-key
									key={index}
									index={index}
									title={item.title}
								/>
							);
						}

						return renderMenuItem(item, index, {
							'data-hovered': activeIndex === index ? true : undefined,
							'aria-selected': activeIndex === index ? true : undefined,
							'data-testid': `${item.name}-menu-item-${index}`,
							...getItemProps({
								active: activeIndex === index,
								role: 'option',
								id: `${item.name}-menu-item-${index}`,
								ref: (node: HTMLButtonElement) => {
									elementsRef.current[index] = node;
								},
								onClick: () => handleItemClick(item),
							}),
						});
					})}
				</Box>
			)}
			{uploadFile && (
				<VisuallyHidden>
					<label>
						<input
							aria-label="Import document"
							type="file"
							ref={fileUploadInputRef}
							onChange={handleFilePicked}
						/>
					</label>
				</VisuallyHidden>
			)}
		</>
	);
}

export function CommandMenu(props: CommandMenuProps) {
	const { onClose, id, view } = props;

	return (
		<FloatingToolbar
			onClose={onClose}
			view={view}
			placement="top-start"
			menu-id={id}
		>
			<CommandMenuInternal {...props} />
		</FloatingToolbar>
	);
}
